import React, { useCallback, useEffect, useMemo, useState } from "react";
import cx from "classnames";
import { AnimatePresence, motion } from "framer-motion";
import { useHistory, useParams } from "react-router-dom";

import { View } from "../storage/routes";

import "./AssetDetails.scss";
import { Skeleton } from "@mui/material";

import {
  getAsset,
  getAssetBackgroundImage,
  getAssetDescription,
  getAssetDuration,
  getAssetGenres,
  getAssetLanguages,
  getAssetMinAge,
  getAssetTitle,
  getAssetYear,
} from "../services/asset";
import { fade } from "../storage/motionAnimations";
import { GreyButton, GreyOutlinedButton } from "../Components/Buttons";
import { FavoriteBorder, PlayCircleFilled } from "@mui/icons-material";
import { displayModal, displayUI } from "../storage/emitter";
import { Swiper, SwiperSlide } from "swiper/react";
import { SRSlide } from "../Components/Slides";

const AssetDetails = () => {
  const history = useHistory();
  const { partner, id: assetID } = useParams();
  const [asset, setAsset] = useState(null);

  const [imgLoaded, setImgLoaded] = useState(false);

  const backgroundImage = useMemo(
    () => (asset ? getAssetBackgroundImage(asset, "1920x1080") : ""),
    [asset]
  );

  const onBackClick = useCallback(() => {
    history.goBack();
  }, [asset]);

  const onPlayClick = useCallback(() => {
    history.push(`/player/${partner}/${asset.id}`);
  }, [asset]);

  const onFavoriteClick = useCallback(() => {
    displayModal("demo");
  }, [asset]);

  const onImageLoad = useCallback(() => {
    setImgLoaded(true);
  }, [backgroundImage]);

  useEffect(() => {
    getAsset(partner, assetID).then((asset) => {
      console.log("Asset:", asset);
      setAsset(asset);
    });
  }, [partner, assetID]);

  const genres = useMemo(() => getAssetGenres(asset)?.join(", "), [asset]);
  const languages = useMemo(
    () => getAssetLanguages(asset)?.join(", "),
    [asset]
  );
  const year = useMemo(() => getAssetYear(asset), [asset]);
  const minAge = useMemo(() => getAssetMinAge(asset), [asset]);

  return (
    <View>
      <div id="asset-view">
        <div className="asset-background">
          <img
            className="asset-image"
            src={backgroundImage}
            onLoad={onImageLoad}
            alt="Asset Cover"
          />
          <AnimatePresence>
            {!imgLoaded && (
              <motion.div
                variants={fade}
                initial="visible"
                animate="visible"
                exit="exit"
              >
                <Skeleton
                  className="asset-image-skelly"
                  animation="wave"
                  variant="rectangular"
                />
              </motion.div>
            )}
          </AnimatePresence>
          <div className="asset-bg-overlay" />
        </div>
        <div className="asset-details" data-assetid={assetID}>
          <div className="back-button" onClick={onBackClick} />
          {asset && (
            <div className="asset-content" data-assetid={asset.id}>
              <h1 className="asset-title">{getAssetTitle(asset)}</h1>
              <div className="asset-info">
                {genres && (
                  <>
                    <div className="asset-genres">{genres}</div>
                    <div className="info-divider" />
                  </>
                )}
                {year && <div className="asset-year">{year}</div>}
                <div className="asset-duration">
                  {getAssetDuration(asset)} Min
                </div>
                {minAge && <div className="asset-age">{minAge}</div>}
              </div>
              {languages && (
                <div className="asset-languages">
                  <span className="languages-label">Languages:</span>
                  <span className="languages-list">{languages}</span>
                </div>
              )}
              <div className="asset-description">
                {getAssetDescription(asset)}
              </div>
              <div className="buttons">
                <GreyButton
                  onClick={onPlayClick}
                  color="secondary"
                  variant="contained"
                  className="button"
                  startIcon={<PlayCircleFilled />}
                >
                  Play
                </GreyButton>
                <GreyOutlinedButton
                  onClick={onFavoriteClick}
                  color="secondary"
                  variant="outlined"
                  className="button"
                  startIcon={<FavoriteBorder />}
                >
                  My List
                </GreyOutlinedButton>
              </div>
            </div>
          )}
          {asset?.related?.length > 0 && (
            <div className="asset-related">
              <div className="slider">
                <div className="slider-title">Related content</div>
                <Swiper
                  key={module.id}
                  slidesPerView={"auto"}
                  spaceBetween={22}
                  centeredSlides={false}
                  loop={false}
                  pagination={false}
                  navigation={false}
                  className="slider-content"
                >
                  {asset?.related?.map((asset) => {
                    return (
                      <SwiperSlide key={asset.id} className={"slider-slide"}>
                        <SRSlide asset={asset} />
                      </SwiperSlide>
                    );
                  })}
                </Swiper>
              </div>
            </div>
          )}
        </div>
      </div>
    </View>
  );
};

export default AssetDetails;
